import { useCallback, useContext, useMemo, useState } from 'react';
import DateRangeIcon from '@mui/icons-material/DateRange';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import { Box, Popover, Typography } from '@mui/material';

import { PARENT } from '../../NewInvoice/InvoiceTable/TableConstants';
import { SiteFinanceTableContext } from '../SiteFinanceContext';

import { QuickStatsPopover } from './QuickStatsPopover/QuickStatsPopover';
import { SiteFinanceInfoItem } from './SiteFinanceInfoItem/SiteFinanceInfoItem';
import { WithholdingAmountsPerAgedARPopover } from './WithholdingAmountsPerAgedARPopover/WithholdingAmountsPerAgedARPopover';
import {
  calculateUnpaidAmountForEvents,
  filterEventsByInvoiceDateRangeInDays,
  isWithholdingType,
  prepareDataForCalculation
} from './SiteFinanceInfoServices';

export const SiteFinanceInfo = () => {
  const { tableData: unpreparedData, allStudiesFms, userActiveRoleIsGlobal } = useContext(SiteFinanceTableContext);

  const tableData = useMemo(() => {
    return prepareDataForCalculation(unpreparedData);
  }, [unpreparedData]);

  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handlePopoverClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const userAbleToSeeARInfo = useMemo(() => {
    return allStudiesFms || userActiveRoleIsGlobal;
  }, [allStudiesFms, userActiveRoleIsGlobal]);

  const popoverProps = useMemo(() => {
    return {
      sx: { pointerEvents: 'none' },
      anchorEl: anchorEl,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left'
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left'
      },
      onClose: handlePopoverClose,
      disableRestoreFocus: true
    };
  }, [anchorEl, handlePopoverClose]);

  const totalPayment = useMemo(() => {
    return tableData
      .filter(ledgerEvent => ['PAID', 'APPLIED'].includes(ledgerEvent.sitePaymentStatus))
      .reduce((acc, curr) => acc + curr.sitePaidAmount, 0);
  }, [tableData]);

  const parentEventsForEdgedAccountReceivable = useMemo(() => {
    return tableData.filter(ledgerEvent => {
      const isPaidStatus = ledgerEvent.reconcileTotalAmountPaid >= ledgerEvent.invoiceAmount;
      return ledgerEvent.sitePaymentType === PARENT && isPaidStatus === false && !!ledgerEvent.invoiceDate;
    });
  }, [tableData]);

  const unpaidInvoice = useMemo(() => {
    if (parentEventsForEdgedAccountReceivable.length === 0) {
      return 0;
    }
    return calculateUnpaidAmountForEvents([...parentEventsForEdgedAccountReceivable]);
  }, [parentEventsForEdgedAccountReceivable]);

  const unpaidInvoiceAmountByDateRange = useCallback(
    (start, end) => {
      if (parentEventsForEdgedAccountReceivable.length === 0) {
        return 0;
      }
      const filteredEvents = filterEventsByInvoiceDateRangeInDays(parentEventsForEdgedAccountReceivable, start, end);
      return calculateUnpaidAmountForEvents([...filteredEvents]);
    },
    [parentEventsForEdgedAccountReceivable]
  );

  const earnedSiteAmount = useMemo(() => {
    return tableData
      .filter(ledgerEvent => !isWithholdingType(ledgerEvent.sitePaymentType))
      .reduce((acc, curr) => acc + curr.siteAmount, 0);
  }, [tableData]);

  const unpaidInvoiceAmountsByDateRange = useMemo(() => {
    return {
      unpaidInvoice030Days: userAbleToSeeARInfo ? unpaidInvoiceAmountByDateRange(0, 30) : 0,
      unpaidInvoice3060Days: userAbleToSeeARInfo ? unpaidInvoiceAmountByDateRange(30, 60) : 0,
      unpaidInvoice6090Days: userAbleToSeeARInfo ? unpaidInvoiceAmountByDateRange(60, 90) : 0,
      unpaidInvoice90Days: userAbleToSeeARInfo ? unpaidInvoiceAmountByDateRange(90, 0) : 0
    };
  }, [unpaidInvoiceAmountByDateRange, userAbleToSeeARInfo]);

  const {
    unpaidInvoice030Days,
    unpaidInvoice3060Days,
    unpaidInvoice6090Days,
    unpaidInvoice90Days
  } = unpaidInvoiceAmountsByDateRange;

  return (
    <Box sx={{ padding: '0 25px' }}>
      <Box
        sx={{
          paddingTop: '8px'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            lineHeight: '45px'
          }}
        >
          Quick Stats
        </Typography>
        <Box
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          id="quick-stats"
          data-testid="quick-stats"
          sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            width: 'fit-content',
            paddingBottom: '8px'
          }}
        >
          <SiteFinanceInfoItem icon={<MonetizationOnIcon />} title="Earned Site Amount:" amount={earnedSiteAmount} />
          <SiteFinanceInfoItem icon={<MonetizationOnIcon />} title="Total Payment:" amount={totalPayment} />
        </Box>
      </Box>

      <Popover {...popoverProps} open={anchorEl?.id === 'quick-stats'} data-testid="quck-stats-popover">
        <QuickStatsPopover />
      </Popover>
      {userAbleToSeeARInfo && (
        <Box
          sx={{
            paddingTop: '8px'
          }}
        >
          <Typography
            variant="h6"
            sx={{
              lineHeight: '45px'
            }}
          >
            Aged Accounts Receivable
          </Typography>
          <Box
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            id="aged-account-receivable"
            data-testid="aged-account-receivable"
            sx={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap',
              width: 'fit-content',
              paddingBottom: '8px'
            }}
          >
            <SiteFinanceInfoItem icon={<MoneyOffIcon />} title="Unpaid Invoice:" amount={unpaidInvoice} />
            <SiteFinanceInfoItem icon={<DateRangeIcon />} title="0-30 Days:" amount={unpaidInvoice030Days} />
            <SiteFinanceInfoItem icon={<DateRangeIcon />} title="30-60 Days:" amount={unpaidInvoice3060Days} />
            <SiteFinanceInfoItem icon={<DateRangeIcon />} title="60-90 Days:" amount={unpaidInvoice6090Days} />
            <SiteFinanceInfoItem icon={<DateRangeIcon />} title=">90 Days:" amount={unpaidInvoice90Days} />
          </Box>
        </Box>
      )}
      <Popover
        {...popoverProps}
        open={anchorEl?.id === 'aged-account-receivable'}
        data-testid="aged-account-receivable-popover"
      >
        <WithholdingAmountsPerAgedARPopover tableData={tableData} />
      </Popover>
    </Box>
  );
};
